// CourseDetailBeautician.js - Beautician Course Detail component
import { DEVICES } from '../../../constants/breakpoints';

export const createCourseDetailBeauticianElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const courseDetailSectionClass = 'course-detail-beautician-section';
  const courseDetailContainerClass = 'course-detail-beautician-container';
  const courseDetailHeaderClass = 'course-detail-beautician-header';
  const courseDetailTitleClass = 'course-detail-beautician-title';
  const courseDetailSubtitleClass = 'course-detail-beautician-subtitle';
  const courseDetailBadgeClass = 'course-detail-beautician-badge';
  const courseDetailContentClass = 'course-detail-beautician-content';
  const courseDetailMainClass = 'course-detail-beautician-main';
  const courseDetailSidebarClass = 'course-detail-beautician-sidebar';
  const courseDetailCardClass = 'course-detail-beautician-card';
  const courseDetailButtonClass = 'course-detail-beautician-button';
  const courseDetailWhatsappButtonClass = 'course-detail-beautician-whatsapp-button';
  const courseDetailButtonsContainerClass = 'course-detail-beautician-buttons-container';

  // Desktop Styles (same as other courses)
  const courseDetailSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.06) 0%, transparent 60%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)
    `,
    color: '#1e293b',
    width: '100%',
    overflow: 'hidden'
  };

  const courseDetailContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const courseDetailHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const courseDetailTitleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: '1.1'
  };

  const courseDetailSubtitleStyles = {
    fontSize: '20px',
    color: '#64748b',
    maxWidth: '800px',
    margin: '0 auto 30px',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const courseDetailBadgeStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    color: '#6366f1',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    border: '1px solid rgba(99, 102, 241, 0.2)'
  };

  const courseDetailContentStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '60px',
    alignItems: 'start'
  };

  const courseDetailMainStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const courseDetailSidebarStyles = {
    position: 'sticky',
    top: '120px'
  };

  const courseDetailCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    padding: '40px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease'
  };

  const courseDetailButtonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    flex: '1'
  };

  const courseDetailWhatsappButtonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#25d366',
    color: '#ffffff',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    flex: '1'
  };

  const courseDetailButtonsContainerStyles = {
    display: 'flex',
    gap: '12px',
    width: '100%'
  };

  // Tablet Styles
  const courseDetailTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const courseDetailTitleTabletStyles = {
    fontSize: '48px'
  };

  const courseDetailContentTabletStyles = {
    gridTemplateColumns: '1fr',
    gap: '40px'
  };

  const courseDetailCardTabletStyles = {
    padding: '30px'
  };

  // Mobile Styles
  const courseDetailMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const courseDetailTitleMobileStyles = {
    fontSize: '36px'
  };

  const courseDetailSubtitleMobileStyles = {
    fontSize: '18px'
  };

  const courseDetailCardMobileStyles = {
    padding: '24px'
  };

  const courseDetailButtonsContainerMobileStyles = {
    flexDirection: 'column',
    gap: '12px'
  };

  // Update registry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(courseDetailSectionClass, courseDetailSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailContainerClass, courseDetailContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailHeaderClass, courseDetailHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailTitleClass, courseDetailTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailSubtitleClass, courseDetailSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailBadgeClass, courseDetailBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailContentClass, courseDetailContentStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailMainClass, courseDetailMainStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailSidebarClass, courseDetailSidebarStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailCardClass, courseDetailCardStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailButtonClass, courseDetailButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailWhatsappButtonClass, courseDetailWhatsappButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDetailButtonsContainerClass, courseDetailButtonsContainerStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(courseDetailSectionClass, courseDetailTabletStyles);
    newRegistry[DEVICES.TABLET].set(courseDetailTitleClass, courseDetailTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(courseDetailContentClass, courseDetailContentTabletStyles);
    newRegistry[DEVICES.TABLET].set(courseDetailCardClass, courseDetailCardTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(courseDetailSectionClass, courseDetailMobileStyles);
    newRegistry[DEVICES.MOBILE].set(courseDetailTitleClass, courseDetailTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(courseDetailSubtitleClass, courseDetailSubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(courseDetailCardClass, courseDetailCardMobileStyles);
    newRegistry[DEVICES.MOBILE].set(courseDetailButtonsContainerClass, courseDetailButtonsContainerMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    courseDetailSectionClass, courseDetailContainerClass, courseDetailHeaderClass,
    courseDetailTitleClass, courseDetailSubtitleClass, courseDetailBadgeClass,
    courseDetailContentClass, courseDetailMainClass, courseDetailSidebarClass,
    courseDetailCardClass, courseDetailButtonClass, courseDetailWhatsappButtonClass,
    courseDetailButtonsContainerClass
  ]));

  // Generate IDs
  const baseId = Date.now();
  const sectionId = baseId;
  const containerId = baseId + 1;
  const headerId = baseId + 2;
  const titleId = baseId + 3;
  const subtitleId = baseId + 4;
  const badgeId = baseId + 5;
  const contentId = baseId + 6;
  const mainId = baseId + 7;
  const sidebarId = baseId + 8;
  const enrollCardId = baseId + 9;
  const enrollButtonId = baseId + 10;
  const enrollWhatsappButtonId = baseId + 11;
  const enrollButtonsContainerId = baseId + 12;
  const overviewCardId = baseId + 13;
  const curriculumCardId = baseId + 14;
  const instructorCardId = baseId + 15;

  // Enrollment card elements
  const enrollPricingId = baseId + 20;
  const enrollPriceId = baseId + 21;
  const enrollOriginalPriceId = baseId + 22;
  const enrollDiscountId = baseId + 23;
  const enrollFeaturesId = baseId + 24;
  const enrollFeature1Id = baseId + 25;
  const enrollFeature2Id = baseId + 26;
  const enrollFeature3Id = baseId + 27;
  const enrollFeature4Id = baseId + 28;

  // Overview card elements
  const overviewTitleId = baseId + 30;
  const overviewDescId = baseId + 31;
  const overviewStatsId = baseId + 32;
  const overviewStat1Id = baseId + 33;
  const overviewStat2Id = baseId + 34;
  const overviewStat3Id = baseId + 35;

  // Curriculum card elements
  const curriculumTitleId = baseId + 40;
  const curriculumContentId = baseId + 41;
  const module1Id = baseId + 42;
  const module2Id = baseId + 43;
  const module3Id = baseId + 44;
  const moreModulesId = baseId + 45;

  // Instructor card elements
  const instructorTitleId = baseId + 50;
  const instructorContentId = baseId + 51;
  const instructorImageId = baseId + 52;
  const instructorInfoId = baseId + 53;
  const instructorNameId = baseId + 54;
  const instructorRoleId = baseId + 55;
  const instructorBioId = baseId + 56;
  const instructorStatsId = baseId + 57;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Beautician Course Detail Section',
      parentId: null,
      className: courseDetailSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'course-detail-section',
        'data-component': 'section',
        'data-section-type': 'course-detail',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Detail Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'course-detail-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Detail Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailHeaderClass,
      styles: {},
      children: [badgeId, titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'course-detail-header'
      }
    },

    // Badge
    {
      id: badgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Badge',
      parentId: headerId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailBadgeClass,
      styles: {},
      children: [],
      text: '💄 Beauty & Wellness Course',
      attributes: {
        'data-element': 'course-badge'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Course Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: courseDetailTitleClass,
      styles: {},
      children: [],
      text: 'Professional Beautician & Cosmetology',
      attributes: {
        'data-element': 'course-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Course Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: courseDetailSubtitleClass,
      styles: {},
      children: [],
      text: 'Master skincare, makeup artistry, hair styling, nail care, and beauty treatments. Learn professional techniques and start your career in the beauty industry.',
      attributes: {
        'data-element': 'course-subtitle'
      }
    },

    // Content Grid
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Content Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailContentClass,
      styles: {},
      children: [mainId, sidebarId],
      text: '',
      attributes: {
        'data-element': 'course-content-grid'
      }
    },

    // Main Content
    {
      id: mainId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Main Content',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailMainClass,
      styles: {},
      children: [overviewCardId, curriculumCardId, instructorCardId],
      text: '',
      attributes: {
        'data-element': 'course-main-content'
      }
    },

    // Sidebar
    {
      id: sidebarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Sidebar',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailSidebarClass,
      styles: {},
      children: [enrollCardId],
      text: '',
      attributes: {
        'data-element': 'course-sidebar'
      }
    },

    // Enrollment Card
    {
      id: enrollCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Card',
      parentId: sidebarId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailCardClass,
      styles: {},
      children: [enrollPricingId, enrollFeaturesId, enrollButtonsContainerId],
      text: '',
      attributes: {
        'data-element': 'enrollment-card'
      }
    },

    // Enrollment Pricing Section
    {
      id: enrollPricingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Pricing',
      parentId: enrollCardId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        textAlign: 'center',
        marginBottom: '30px'
      },
      children: [enrollPriceId, enrollOriginalPriceId, enrollDiscountId],
      text: '',
      attributes: {
        'data-element': 'enrollment-pricing'
      }
    },

    // Current Price
    {
      id: enrollPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Current Price',
      parentId: enrollPricingId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        fontSize: '48px',
        fontWeight: '700',
        color: '#6366f1',
        marginBottom: '8px'
      },
      children: [],
      text: '$0',
      attributes: {
        'data-element': 'current-price'
      }
    },

    // Original Price
    {
      id: enrollOriginalPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Original Price',
      parentId: enrollPricingId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        fontSize: '16px',
        color: '#64748b',
        textDecoration: 'line-through'
      },
      children: [],
      text: '$165',
      attributes: {
        'data-element': 'original-price'
      }
    },

    // Discount Badge
    {
      id: enrollDiscountId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Discount Badge',
      parentId: enrollPricingId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        fontSize: '14px',
        color: '#059669',
        fontWeight: '600',
        marginTop: '8px'
      },
      children: [],
      text: '34% OFF - Limited Time',
      attributes: {
        'data-element': 'discount-badge'
      }
    },

    // Enrollment Features
    {
      id: enrollFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Features',
      parentId: enrollCardId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        marginBottom: '30px'
      },
      children: [enrollFeature1Id, enrollFeature2Id, enrollFeature3Id, enrollFeature4Id],
      text: '',
      attributes: {
        'data-element': 'enrollment-features'
      }
    },

    // Feature 1
    {
      id: enrollFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 1',
      parentId: enrollFeaturesId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      },
      children: [],
      text: '✓ Lifetime Access',
      attributes: {
        'data-element': 'enrollment-feature'
      }
    },

    // Feature 2
    {
      id: enrollFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 2',
      parentId: enrollFeaturesId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      },
      children: [],
      text: '✓ 11+ Hours of Content',
      attributes: {
        'data-element': 'enrollment-feature'
      }
    },

    // Feature 3
    {
      id: enrollFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 3',
      parentId: enrollFeaturesId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      },
      children: [],
      text: '✓ Certificate of Completion',
      attributes: {
        'data-element': 'enrollment-feature'
      }
    },

    // Feature 4
    {
      id: enrollFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 4',
      parentId: enrollFeaturesId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      },
      children: [],
      text: '✓ Professional Kit Guide',
      attributes: {
        'data-element': 'enrollment-feature'
      }
    },

    // Enroll Buttons Container
    {
      id: enrollButtonsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Buttons Container',
      parentId: enrollCardId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailButtonsContainerClass,
      styles: {},
      children: [enrollButtonId, enrollWhatsappButtonId],
      text: '',
      attributes: {
        'data-element': 'enrollment-buttons-container'
      }
    },

    // Enroll Button
    {
      id: enrollButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Enroll Button',
      parentId: enrollButtonsContainerId,
      hierarchyName: getHierarchyName('button'),
      className: courseDetailButtonClass,
      styles: {},
      children: [],
      text: '🌐 Enroll via Website',
      attributes: {
        'data-element': 'enroll-button'
      },
      linkTo: 'enrollment'
    },

    // Enroll WhatsApp Button
    {
      id: enrollWhatsappButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Enroll WhatsApp Button',
      parentId: enrollButtonsContainerId,
      hierarchyName: getHierarchyName('button'),
      className: courseDetailWhatsappButtonClass,
      styles: {},
      children: [],
      text: '📱 Enroll via WhatsApp',
      attributes: {
        'data-element': 'whatsapp-button',
        'data-whatsapp-phone': '+923458991171',
        'data-whatsapp-message': 'Hi! I want to enroll in the Professional Beautician & Cosmetology course. Can you help me with the enrollment process?'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 2,
            type: 'click',
            action: 'whatsapp-btn-click',
            label: 'Order via WhatsApp'
          }
        ]
      }
    },

    // Overview Card
    {
      id: overviewCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Overview Card',
      parentId: mainId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailCardClass,
      styles: {},
      children: [overviewTitleId, overviewDescId, overviewStatsId],
      text: '',
      attributes: {
        'data-element': 'course-overview-card'
      }
    },

    // Overview Title
    {
      id: overviewTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Overview Title',
      parentId: overviewCardId,
      hierarchyName: getHierarchyName('h2'),
      styles: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '24px',
        fontFamily: '"Inter", sans-serif'
      },
      children: [],
      text: 'Course Overview',
      attributes: {
        'data-element': 'overview-title'
      }
    },

    // Overview Description
    {
      id: overviewDescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Overview Description',
      parentId: overviewCardId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '16px',
        color: '#64748b',
        lineHeight: '1.6',
        marginBottom: '30px'
      },
      children: [],
      text: 'This comprehensive beautician course covers all aspects of professional beauty services including skincare, makeup, hair styling, nail care, and spa treatments. Learn industry-standard techniques and build a successful beauty career.',
      attributes: {
        'data-element': 'overview-description'
      }
    },

    // Overview Statistics Grid
    {
      id: overviewStatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Overview Statistics',
      parentId: overviewCardId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px'
      },
      children: [overviewStat1Id, overviewStat2Id, overviewStat3Id],
      text: '',
      attributes: {
        'data-element': 'overview-statistics'
      }
    },

    // Stat 1 - Hours
    {
      id: overviewStat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hours Stat',
      parentId: overviewStatsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        textAlign: 'center',
        padding: '20px',
        background: '#f8fafc',
        borderRadius: '12px'
      },
      children: [],
      text: '11+ Hours of Content',
      attributes: {
        'data-element': 'overview-stat'
      }
    },

    // Stat 2 - Techniques
    {
      id: overviewStat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Techniques Stat',
      parentId: overviewStatsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        textAlign: 'center',
        padding: '20px',
        background: '#f8fafc',
        borderRadius: '12px'
      },
      children: [],
      text: '25+ Beauty Techniques',
      attributes: {
        'data-element': 'overview-stat'
      }
    },

    // Stat 3 - Modules
    {
      id: overviewStat3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Modules Stat',
      parentId: overviewStatsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        textAlign: 'center',
        padding: '20px',
        background: '#f8fafc',
        borderRadius: '12px'
      },
      children: [],
      text: '7 Core Modules',
      attributes: {
        'data-element': 'overview-stat'
      }
    },

    // Curriculum Card
    {
      id: curriculumCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Curriculum Card',
      parentId: mainId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailCardClass,
      styles: {},
      children: [curriculumTitleId, curriculumContentId],
      text: '',
      attributes: {
        'data-element': 'course-curriculum-card'
      }
    },

    // Curriculum Title
    {
      id: curriculumTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Curriculum Title',
      parentId: curriculumCardId,
      hierarchyName: getHierarchyName('h2'),
      styles: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '24px',
        fontFamily: '"Inter", sans-serif'
      },
      children: [],
      text: 'Course Curriculum',
      attributes: {
        'data-element': 'curriculum-title'
      }
    },

    // Curriculum Content
    {
      id: curriculumContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Curriculum Content',
      parentId: curriculumCardId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      },
      children: [module1Id, module2Id, module3Id, moreModulesId],
      text: '',
      attributes: {
        'data-element': 'curriculum-content'
      }
    },

    // Module 1
    {
      id: module1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Module 1',
      parentId: curriculumContentId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px'
      },
      children: [],
      text: 'Module 1: Skincare Fundamentals - Skin Analysis & Types (30 min), Cleansing & Exfoliation Techniques (25 min), Facial Treatments & Masks (20 min)',
      attributes: {
        'data-element': 'curriculum-module'
      }
    },

    // Module 2
    {
      id: module2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Module 2',
      parentId: curriculumContentId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px'
      },
      children: [],
      text: 'Module 2: Makeup Artistry - Foundation & Base Application (35 min), Eye Makeup Techniques (30 min), Contouring & Highlighting (25 min)',
      attributes: {
        'data-element': 'curriculum-module'
      }
    },

    // Module 3
    {
      id: module3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Module 3',
      parentId: curriculumContentId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px'
      },
      children: [],
      text: 'Module 3: Hair Styling & Care - Hair Types & Treatments (25 min), Basic Cutting & Styling (35 min), Hair Coloring Basics (20 min)',
      attributes: {
        'data-element': 'curriculum-module'
      }
    },

    // More Modules
    {
      id: moreModulesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'More Modules',
      parentId: curriculumContentId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        textAlign: 'center',
        padding: '20px',
        color: '#64748b',
        fontStyle: 'italic'
      },
      children: [],
      text: '+ 4 More Comprehensive Modules (Nail Care & Art, Spa Treatments, Client Consultation, Business Setup)',
      attributes: {
        'data-element': 'more-modules'
      }
    },

    // Instructor Card
    {
      id: instructorCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Course Instructor Card',
      parentId: mainId,
      hierarchyName: getHierarchyName('div'),
      className: courseDetailCardClass,
      styles: {},
      children: [instructorTitleId, instructorContentId],
      text: '',
      attributes: {
        'data-element': 'course-instructor-card'
      }
    },

    // Instructor Title
    {
      id: instructorTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Instructor Title',
      parentId: instructorCardId,
      hierarchyName: getHierarchyName('h2'),
      styles: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '24px',
        fontFamily: '"Inter", sans-serif'
      },
      children: [],
      text: 'Meet Your Instructor',
      attributes: {
        'data-element': 'instructor-title'
      }
    },

    // Instructor Content Container
    {
      id: instructorContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Instructor Content',
      parentId: instructorCardId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        gap: '24px',
        alignItems: 'start'
      },
      children: [instructorImageId, instructorInfoId],
      text: '',
      attributes: {
        'data-element': 'instructor-content'
      }
    },

    // Instructor Image
    {
      id: instructorImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instructor Image',
      parentId: instructorContentId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '4px solid #6366f1'
      },
      src: 'https://images.unsplash.com/photo-1594824388853-e0c8b8b0b6e5?w=120&h=120&fit=crop&crop=center',
      children: [],
      attributes: {
        'data-element': 'instructor-image',
        'alt': 'Zara Ahmed'
      }
    },

    // Instructor Info
    {
      id: instructorInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Instructor Info',
      parentId: instructorContentId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        flex: '1'
      },
      children: [instructorNameId, instructorRoleId, instructorBioId, instructorStatsId],
      text: '',
      attributes: {
        'data-element': 'instructor-info'
      }
    },

    // Instructor Name
    {
      id: instructorNameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Instructor Name',
      parentId: instructorInfoId,
      hierarchyName: getHierarchyName('h3'),
      styles: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px'
      },
      children: [],
      text: 'Zara Ahmed',
      attributes: {
        'data-element': 'instructor-name'
      }
    },

    // Instructor Role
    {
      id: instructorRoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Instructor Role',
      parentId: instructorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '16px',
        color: '#6366f1',
        marginBottom: '16px',
        fontWeight: '500'
      },
      children: [],
      text: 'Licensed Cosmetologist & Beauty Academy Director',
      attributes: {
        'data-element': 'instructor-role'
      }
    },

    // Instructor Bio
    {
      id: instructorBioId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Instructor Bio',
      parentId: instructorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#64748b',
        lineHeight: '1.6',
        marginBottom: '20px'
      },
      children: [],
      text: 'Zara has over 16 years of experience in the beauty industry and runs one of the most successful beauty academies in the region. She has trained thousands of beauticians and makeup artists who now work in top salons and spas.',
      attributes: {
        'data-element': 'instructor-bio'
      }
    },

    // Instructor Stats
    {
      id: instructorStatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Instructor Stats',
      parentId: instructorInfoId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        gap: '20px',
        fontSize: '14px',
        color: '#64748b'
      },
      children: [],
      text: '4,200+ Students Trained • 4.9/5 Instructor Rating • 16+ Years Experience',
      attributes: {
        'data-element': 'instructor-stats'
      }
    }
  ];

  return {
    courseDetailSection: newElements[0],
    elements: newElements
  };
}; 